import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CKEditor from "ckeditor4-react";
import EmailImageInsertMenu from "../../notificationManagement/EmailImageInsertMenu";
import {
  attachCustomImageInsertBehavior,
  bindNotificationImageMenuOpener,
  defaultNotificationImageUploadHandler,
  getNotificationEmailEditorConfig,
  insertImageIntoEditor,
} from "../../notificationManagement/emailEditorUtils";

const editorWrapperSx = {
  width: "100%",
  minWidth: 0,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  overflow: "visible",
  backgroundColor: "background.paper",
  "& .cke_chrome": {
    border: "none !important",
    boxShadow: "none !important",
  },
  "& .cke_top": {
    borderBottom: "1px solid rgba(0,0,0,0.12) !important",
    background: "#fafafa !important",
    overflowX: "auto",
    padding: "4px 6px !important",
  },
  "& .cke_bottom": {
    display: "none",
  },
  "& .cke_inner": {
    display: "flex !important",
    flexDirection: "column !important",
  },
  "& .cke_contents": {
    minHeight: { xs: 200, sm: 320 },
    height: { xs: 200, sm: 320 },
  },
  "& .cke_contents iframe": {
    height: "100% !important",
    minHeight: "100% !important",
  },
  "& .cke_toolbox": {
    display: "flex !important",
    flexWrap: "nowrap !important",
    alignItems: "center !important",
    width: "100%",
    float: "none !important",
  },
  "& .cke_toolbar_break": {
    display: "none !important",
  },
  "& .cke_toolbar": {
    display: "inline-flex !important",
    flexWrap: "nowrap !important",
    alignItems: "center !important",
    float: "none !important",
    margin: "0 !important",
  },
  "& .cke_toolgroup": {
    display: "inline-flex !important",
    flexWrap: "nowrap !important",
    alignItems: "center !important",
    float: "none !important",
    margin: "0 2px 0 0 !important",
  },
  "& .cke_button": {
    float: "none !important",
  },
};

const RichTextEditor = ({
  value = "",
  onChange,
  onEditorReady,
  imageUploadHandler = defaultNotificationImageUploadHandler,
  onImageUploadError,
  showImageInsertMenu = true,
  configOverrides = {},
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const editorRef = useRef(null);
  const imageMenuRef = useRef(null);
  const openImageMenuRef = useRef(null);

  openImageMenuRef.current = (anchorEl) => {
    imageMenuRef.current?.openMenu(anchorEl);
  };

  useEffect(() => {
    if (!showImageInsertMenu) {
      bindNotificationImageMenuOpener(null);
      return undefined;
    }
    bindNotificationImageMenuOpener((anchorEl) => {
      openImageMenuRef.current?.(anchorEl);
    });
    return () => bindNotificationImageMenuOpener(null);
  }, [showImageInsertMenu]);

  const editorConfig = useMemo(
    () => ({
      ...getNotificationEmailEditorConfig(isMobile, showImageInsertMenu),
      ...configOverrides,
    }),
    [isMobile, configOverrides, showImageInsertMenu]
  );

  const handleInstanceReady = useCallback(
    (evt) => {
      editorRef.current = evt.editor;
      if (typeof onEditorReady === "function") {
        onEditorReady(evt.editor);
      }
      if (showImageInsertMenu) {
        attachCustomImageInsertBehavior(evt.editor, (anchorEl) => {
          openImageMenuRef.current?.(anchorEl);
        });
      }
    },
    [onEditorReady, showImageInsertMenu]
  );

  const handleChange = useCallback(
    (evt) => {
      if (typeof onChange === "function") {
        onChange(evt.editor.getData());
      }
    },
    [onChange]
  );

  const handleInsertImage = useCallback(
    (url, displayName) => {
      const editor = editorRef.current;
      if (!editor) return;
      insertImageIntoEditor(editor, url, displayName);
      if (typeof onChange === "function") {
        onChange(editor.getData());
      }
    },
    [onChange]
  );

  return (
    <Box sx={{ ...editorWrapperSx, ...sx }}>
      {showImageInsertMenu && (
        <EmailImageInsertMenu
          ref={imageMenuRef}
          onInsertImage={handleInsertImage}
          imageUploadHandler={imageUploadHandler}
          onError={onImageUploadError}
        />
      )}
      <CKEditor
        data={value}
        onChange={handleChange}
        onInstanceReady={handleInstanceReady}
        config={editorConfig}
      />
    </Box>
  );
};

export default RichTextEditor;
